"use client";

import {
	RiCalendarCheckFill,
	RiLiveFill,
	RiPushpin2Fill,
} from "@remixicon/react";
import { cn } from "@workspace/ui/lib/utils";
import { memo } from "react";
import type { UserFestivalStatus } from "@/lib/features/festival/user-festival-status";
import {
	getStatusLabel,
	USER_FESTIVAL_STATUS,
} from "@/lib/features/festival/user-festival-status";

interface FestivalStatusDisplayProps {
	currentStatus: UserFestivalStatus | null;
	festivalIsPast?: boolean;
	size?: "sm" | "default";
	className?: string;
}

/**
 * Read-only festival status display for showing other users' festival status
 */
export const FestivalStatusDisplay = memo(function FestivalStatusDisplay({
	currentStatus,
	festivalIsPast = false,
	size = "default",
	className,
}: FestivalStatusDisplayProps) {
	if (!currentStatus) {
		return null;
	}

	const buttonSize = size === "sm" ? "sm" : "default";
	const iconSize = size === "sm" ? "h-4 w-4" : "h-5 w-5";

	const statusConfig = {
		[USER_FESTIVAL_STATUS.WANT_TO_GO]: {
			icon: RiPushpin2Fill,
			colorClass: "bg-muted/50 dark:bg-muted text-foreground",
		},
		[USER_FESTIVAL_STATUS.GOING]: {
			icon: RiCalendarCheckFill,
			colorClass: "bg-radix-green-9 text-radix-green-1",
		},
		[USER_FESTIVAL_STATUS.WENT]: {
			icon: RiCalendarCheckFill,
			colorClass: "bg-radix-green-9 text-radix-green-1",
		},
		[USER_FESTIVAL_STATUS.WATCHED_ONLINE]: {
			icon: RiLiveFill,
			colorClass: "bg-radix-ruby-9 text-radix-ruby-1",
		},
	};

	const config = statusConfig[currentStatus];
	if (!config) {
		return null;
	}

	const Icon = config.icon;
	const label = getStatusLabel(currentStatus, festivalIsPast);

	return (
		<div
			className={cn(
				"inline-flex items-center justify-center gap-2 font-medium transition-colors",
				"rounded-lg",
				config.colorClass,
				buttonSize === "sm" ? "h-8 px-3 text-xs" : "h-10 px-4 py-2",
				className,
			)}
		>
			<Icon className={iconSize} />
			{label}
		</div>
	);
});

FestivalStatusDisplay.displayName = "FestivalStatusDisplay";
